import { createReducer } from '@reduxjs/toolkit';
import { closeEditTranslationComponent, showEditTranslationComponent, translatedFileReplacement } from 'redux/actions/edit-translations';
import { EditableCourseElements } from 'redux/schemas/models/edit-translations';
import { AccordionLectureComponentData, ComponentType, LectureComponent, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { CourseOutlineItem, LectureOutlineItem } from 'redux/selectors/course-outline/course-outline-item';
import { Attachment } from 'redux/schemas/models/attachment';
import { Exercise } from 'redux/schemas/models/exercise';
import { translationUpdate, translationUpdateSections } from 'redux/actions/edit-translations';
import { initialRootState } from '.';

export default createReducer(initialRootState, builder => {
  builder
    .addCase(showEditTranslationComponent, (state, action) => {
      let editViewOptions;
      let dataContent;
      let lectureSectionId;
      let lectureSubsectionId;

      const editableCourseElements = Object.values(EditableCourseElements) as string[];
      if (editableCourseElements.includes(action.payload.type)) {
        dataContent = (action.payload.component as CourseOutlineItem).name;
        if (action.payload.type === EditableCourseElements.LESSON) {
          if ((action.payload.component as LectureOutlineItem).sectionType === 'section') {
            lectureSectionId = (action.payload.component as LectureOutlineItem).section;
          } else {
            lectureSubsectionId = (action.payload.component as LectureOutlineItem).section;
          }
        }
      } else {
        editViewOptions = (action.payload.component as LectureComponent).viewOptions;
        if (action.payload.type === ComponentType.ACCORDION) {
          // For Accordions we need to get the array of sections,
          // and for the rest, it's just text.
          dataContent = (action.payload.component as AccordionLectureComponentData).accordionSections;
        } else {
          dataContent = (action.payload.component as LectureComponent).content;
        }
      }
      const info = {
        type: action.payload.type,
        trueType: action.payload.trueType,
        content: dataContent,
        viewOptions: editViewOptions,
        id: action.payload.component.id,
        // In case it's a course element
        lectureSectionId,
        lectureSubsectionId,
      };
      state.app.editTranslationInfo = info;
    })
    .addCase(closeEditTranslationComponent, (state, action) => {
      state.app.editTranslationInfo = null;
    })
    .addCase(translationUpdate.fulfilled, (state, action) => {
      action.meta.arg.attributes.forEach((attribute) => {
        let elements = null;
        let elementIndex = attribute.elementId;
        switch (attribute.elementType) {
          case ComponentType.ACCORDION_SECTION:
            elements = (state.models.lectureComponents[action.meta.arg.id] as AccordionLectureComponentData).accordionSections;
            break;
          case ComponentType.PUBLIC_PEER_EVALUATION:
          case ComponentType.PRIVATE_PEER_EVALUATION:
            elements = state.models.peerEvaluations;
            break;
          case ComponentType.TEAM_DISCUSSION:
          case ComponentType.TOPIC: {
            state.models.posts[attribute.elementId] = {
              ...state.models.posts[attribute.elementId],
              [attribute.name]: attribute.text,
            };
            elementIndex = null;
            const topicTag = 'topic'; // To avoid parsing and type issues when calling ".topic" directly
            state.models.lectureComponents[action.meta.arg.id][topicTag] = {
              ...state.models.lectureComponents[action.meta.arg.id][topicTag],
              [attribute.name]: attribute.text,
            };
            break;
          }
          case ComponentType.EXERCISE:
            if (attribute.name === 'header' || attribute.name === 'instructions') {
              const lectureComponent = state.models.lectureComponents[action.meta.arg.id] as NLectureComponent<ComponentType.EXERCISE>;
              elements = (state.models.exercises[lectureComponent.exercise] as unknown as Exercise).template.sections;
            } else {
              elements = state.models.exercises;
            }
            break;
          case ComponentType.POLL:
            if (attribute.name === 'title') {
              elements = state.models.polls;
            } else if (attribute.name === 'questionText') {
              elements = state.models.pollQuestions;
            } else {
              elements = state.models.pollOptions;
            }
            break;
          case ComponentType.PROFILE_COMPLETION:
            if (attribute.name === 'content') {
              elements = state.models.lectureComponents;
            } else {
              elements = state.models.profileRequirements;
            }
            break;
          case ComponentType.AUDIO:
          case ComponentType.VIDEO:
            if (attribute.name === 'name') {
              state.models.lectureVideos[attribute.elementId].title = attribute.text;
              elementIndex = state.models.lectureVideos[attribute.elementId].video;
              elements = state.models.video;
            } else {
              elements = state.models.lectureComponents;
            }
            break;
          case ComponentType.SURVEY:
            if (attribute.questionId) {
              if (!attribute.optionId) { // Update a question
                elements = state.models.quizQuestions;
                elementIndex = attribute.questionId;
              } else { // Update an option
                elements = state.models.quizQuestionOptions;
                elementIndex = attribute.optionId;
              }
            } else {
              elements = state.models.surveys;
            }
            break;
          case ComponentType.QUIZ:
          case ComponentType.PROGRESSIVE_QUIZ:
            if (attribute.questionId) {
              if (attribute.feedbackId) { // Update a feedback
                elements = state.models.quizQuestionFeedbacks;
                elementIndex = attribute.feedbackId;
              } else if (attribute.optionId) { // Update an option
                elements = state.models.quizQuestionOptions;
                elementIndex = attribute.optionId;
              } else { // Update a question
                elements = state.models.quizQuestions;
                elementIndex = attribute.questionId;
              }
            } else {
              elements = attribute.elementType === ComponentType.QUIZ ? state.models.quizzes : state.models.progressiveQuizzes;
            }
            break;
          case ComponentType.EXTERNAL_TOOL:
            elements = state.models.externalTools;
            if (attribute.name === 'name') {
              state.models.externalTools[attribute.elementId].title = attribute.text;
            }
            break;
          default:
            elementIndex = action.meta.arg.id;
            elements = state.models.lectureComponents;
        }
        if (elementIndex !== null && elements !== null) {
          elements[elementIndex] = {
            ...elements[elementIndex],
            [attribute.name]: attribute.text,
          };
        }
      });

      if (action.meta.arg.attachments?.length > 0) {
        action.meta.arg.attachments.forEach((attachment, index) => {
          const lectureComponent = state.models.lectureComponents[action.meta.arg.id] as NLectureComponent<ComponentType.EXERCISE>;
          const exercise = state.models.exercises[lectureComponent.exercise];
          const currentAttachmentIndex = exercise.attachments.findIndex(currentAttachment => currentAttachment.id === attachment.translatedFrom);
          const payloadAttachment = action.payload[index];
          if (payloadAttachment) {
            exercise.attachments[currentAttachmentIndex] = {
              ...exercise.attachments[currentAttachmentIndex],
              autoTranslated: {
                lectureComponentId: lectureComponent.id,
                attachmentId: payloadAttachment.id,
                boxDocumentId: payloadAttachment.file.boxDocumentId,
                translatedFromId: attachment.translatedFrom,
                documentName: payloadAttachment.file.name,
                documentUrl: payloadAttachment.file.url,
                errorCode: null,
                language: action.meta.arg.language,
                status: 'completed',
                manuallyTranslated: true,
                thumbnailUrl: payloadAttachment.file.thumbnailUrl,
                size: payloadAttachment.file.size,
              },
            };
          }
        });
      }
      state.models.lectureComponents[action.meta.arg.id] = {
        ...state.models.lectureComponents[action.meta.arg.id],
        aiEdited: true,
      };
    })
    .addCase(translationUpdateSections.fulfilled, (state, action) => {
      if (action.meta.arg.type === EditableCourseElements.LESSON) {
        state.models.lecturePages[action.meta.arg.id] = {
          ...state.models.lecturePages[action.meta.arg.id],
          title: action.meta.arg.attributes[0].text,
          aiEdited: true,
        };
      } else {
        state.models.lectureSections[action.meta.arg.id] = {
          ...state.models.lectureSections[action.meta.arg.id],
          title: action.meta.arg.attributes[0].text,
          aiEdited: true,
        };
      }
    })
    .addCase(translatedFileReplacement.fulfilled, (state, action) => {
      if (action.payload) {
        const { lectureComponentId } = action.meta.arg;
        const payloadAttachment = action.payload;
        const currentAttachment = (state.models.attachments[action.meta.arg.attachmentId] as unknown as Attachment);

        (state.models.attachments[action.meta.arg.attachmentId] as unknown as Attachment) = {
          ...currentAttachment,
          autoTranslated: {
            lectureComponentId,
            attachmentId: payloadAttachment.id,
            boxDocumentId: payloadAttachment.file.boxDocumentId,
            translatedFromId: action.meta.arg.attachmentId,
            documentName: payloadAttachment.file.name,
            documentUrl: payloadAttachment.file.url,
            errorCode: null,
            language: action.meta.arg.language,
            status: 'completed',
            manuallyTranslated: true,
            thumbnailUrl: payloadAttachment.file.thumbnailUrl,
            size: payloadAttachment.file.size,
          },
        };
        state.models.lectureComponents[lectureComponentId] = {
          ...state.models.lectureComponents[lectureComponentId],
          aiEdited: true,
        };
      }
    });
});
